/**
 * Risk Badge Component
 * 
 * Displays a colored badge for risk levels.
 */

import { Shield, AlertTriangle, AlertCircle, CheckCircle2 } from 'lucide-react';
import type { RiskLevel } from '../../data/types';

interface RiskBadgeProps {
  level: RiskLevel | null | undefined;
  showIcon?: boolean;
}

export function RiskBadge({ level, showIcon = true }: RiskBadgeProps) {
  const config = getRiskConfig(level);

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.className}`}
    >
      {showIcon && <config.Icon className="w-3 h-3" />}
      {config.label}
    </span>
  );
}

function getRiskConfig(level: RiskLevel | null | undefined) {
  switch (level) {
    case 'high':
      return {
        label: 'High Risk',
        className: 'bg-accent-red/20 text-accent-red border border-accent-red/30',
        Icon: AlertTriangle,
      };
    case 'medium':
      return {
        label: 'Medium Risk',
        className: 'bg-accent-orange/20 text-accent-orange border border-accent-orange/30',
        Icon: AlertCircle,
      };
    case 'low':
      return {
        label: 'Low Risk',
        className: 'bg-accent-blue/20 text-accent-blue border border-accent-blue/30',
        Icon: Shield,
      };
    case 'none':
      return {
        label: 'No Risk',
        className: 'bg-accent-green/20 text-accent-green border border-accent-green/30',
        Icon: CheckCircle2,
      };
    case 'hidden':
      return {
        label: 'Hidden',
        className: 'bg-bg-tertiary text-text-secondary border border-border-default',
        Icon: Shield,
      };
    default:
      return {
        label: 'Unknown',
        className: 'bg-bg-tertiary text-text-secondary border border-border-default',
        Icon: Shield,
      };
  }
}
